/* ORIO AppShell — warm, light PWA shell (mobile-first, Apple Fitness/Airbnb style).
   Desktop keeps a top nav; mobile uses the bottom tab bar with central SOS. */
import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Map, Briefcase, Shield, Bell, UserCircle2, Heart, Home as HomeIcon, User as UserIcon, HeartHandshake, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import MobileNav from "@/components/MobileNav";
import EmailVerifyBanner from "@/components/EmailVerifyBanner";
import SwUpdateToast from "@/components/SwUpdateToast";
import MyStoriesTrigger from "@/components/MyStoriesTrigger";
import useInboxUnread from "@/hooks/useInboxUnread";
import OnboardingTour from "@/components/OnboardingTour";
import WelcomeNewUserModal from "@/components/WelcomeNewUserModal";
import WelcomeCompleteSheet from "@/components/WelcomeCompleteSheet";
import KarmaBurst from "@/components/KarmaBurst";
import useRadarAlertsWatcher from "@/hooks/useRadarAlertsWatcher";
import PushOptInSheet from "@/components/PushOptInSheet";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();

  // Background watcher: polls ORIO Radar nearby-alerts every 10 minutes,
  // shows toast + (opt-in) system push for new fire/heat/weather events.
  useRadarAlertsWatcher({ enabled: !!user });
  const inboxUnread = useInboxUnread();

  const navItems = [
    { to: "/app",           icon: HomeIcon,       label: "Home",      testid: "nav-home" },
    { to: "/app/mappa",     icon: Map,            label: "Mappa",     testid: "nav-map" },
    { to: "/app/cani",      icon: UserIcon,       label: "Cani",      testid: "nav-dogs" },
    { to: "/app/match",     icon: Heart,          label: "Match",     testid: "nav-match" },
    { to: "/app/missioni",  icon: HeartHandshake, label: "Missioni",  testid: "nav-missions" },
    { to: "/app/cassetto",  icon: Briefcase,      label: "Cassetto",  testid: "nav-drawer" },
    { to: "/app/notifiche", icon: Bell,           label: "Notifiche", testid: "nav-notif" },
    { to: "/app/messaggi",  icon: MessageCircle,  label: "Messaggi",  testid: "nav-messages" },
    { to: "/app/profilo",   icon: UserCircle2,    label: "Profilo",   testid: "nav-profile" },
  ];

  const isActive = (to) =>
    to === "/app" ? loc.pathname === "/app" : loc.pathname.startsWith(to);

  const isWalkPage = loc.pathname.startsWith("/app/passeggiate");
  const isBillingPage = loc.pathname.startsWith("/billing");

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-x-hidden"
      style={{
        background: isWalkPage ? "#050403" : "var(--orio-bg)",
        color: isWalkPage ? "#fff" : "var(--orio-text)",
      }}
    >
      {/* Soft warm glow at top — barely-there brand accent. Hidden on the
          dark walking page where a stronger radial gradient runs inline. */}
      {!isWalkPage && (
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-0 h-[280px] -z-0"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 0%, rgba(255,122,26,0.10) 0%, rgba(244,184,60,0.06) 35%, rgba(0,0,0,0) 80%)",
        }}
      />
      )}

      {/* DESKTOP top header — hidden on mobile (mobile has bottom-nav instead) */}
      {!isBillingPage && (
      <header className="hidden md:block sticky top-0 z-50 bg-white/85 border-b border-[#F0EBE3] backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/app" data-testid="app-logo" className="flex items-center -my-1" title="Vai alla Home app">
            <div aria-label="ORIO" className="h-7 w-[80px] orio-wordmark-grad" />
          </Link>
          <nav className="flex items-center gap-0.5">
            {navItems.map((n) => {
              const active = isActive(n.to);
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  className="group px-3 py-2 rounded-full text-sm transition-colors flex items-center gap-2"
                  style={{
                    color: active ? "#FF7A1A" : "#6B6B6B",
                    background: active ? "#FF7A1A14" : "transparent",
                    fontWeight: active ? 600 : 500,
                  }}
                >
                  <n.icon className="h-4 w-4" strokeWidth={active ? 2.3 : 1.8} /> {n.label}
                </Link>
              );
            })}
            {user?.role === "admin" && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className="ml-1 px-3 py-2 rounded-full text-sm text-[#F4B83C] hover:bg-[#F4B83C]/10 transition-colors flex items-center gap-2 font-medium"
              >
                <Shield className="h-4 w-4" /> Admin
              </Link>
            )}
          </nav>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-semibold text-[#1A1A1A]">{user?.name}</div>
              <div className="text-xs text-[#9A9A9A] flex items-center gap-1.5 justify-end">
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ background: user?.is_subscribed ? "#2DBA60" : user?.in_trial ? "#F4B83C" : "#9A9A9A" }}
                />
                {user?.is_subscribed ? "Abbonato" : user?.in_trial ? "Prova attiva" : "Senza piano"}
              </div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              className="h-9 w-9 rounded-full bg-[#F4EFE6] hover:bg-[#EAE3D2] flex items-center justify-center transition-colors text-[#1A1A1A]"
              aria-label="Esci"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>
      )}

      {/* MOBILE compact top bar — hidden on immersive pages (walk focus mode,
          billing checkout flow). */}
      {!(loc.pathname.startsWith("/app/passeggiate") ||
         loc.pathname.startsWith("/billing")) && (
      <header className="md:hidden sticky top-0 z-40 bg-[#FBF7F1]/92 border-b border-[#F0EBE3] backdrop-blur-md">
        <div className="px-4 h-14 flex items-center justify-between">
          <Link to="/app" data-testid="app-logo" className="flex items-center" title="Vai alla Home app">
            <div aria-label="ORIO" className="h-7 w-[72px] orio-wordmark-grad" />
          </Link>
          <div className="flex items-center gap-1.5">
            <Link
              to="/app/messaggi"
              data-testid="m-messages-btn"
              className="relative h-10 w-10 rounded-full bg-white border border-[#F0EBE3] flex items-center justify-center text-[#1A1A1A] active:bg-[#F4EFE6]"
              aria-label="Messaggi"
            >
              <MessageCircle className="h-[18px] w-[18px]" strokeWidth={1.8} />
              {inboxUnread > 0 && (
                <span
                  data-testid="m-messages-badge"
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center border-2 border-[#FBF7F1]"
                  style={{ background: "#E11D48" }}
                >
                  {inboxUnread > 9 ? "9+" : inboxUnread}
                </span>
              )}
            </Link>
            <Link
              to="/app/notifiche"
              data-testid="m-notif-btn"
              className="h-10 w-10 rounded-full bg-white border border-[#F0EBE3] flex items-center justify-center text-[#1A1A1A] active:bg-[#F4EFE6]"
              aria-label="Notifiche"
            >
              <Bell className="h-[18px] w-[18px]" strokeWidth={1.8} />
            </Link>
            {/* Instagram-style: tap avatar → my Stories (viewer + add).
                Long-press → profilo. */}
            <MyStoriesTrigger />
          </div>
        </div>
      </header>
      )}

      <EmailVerifyBanner />

      {/* Mobile-only bottom padding to clear the fixed MobileNav (~124px above
          viewport + safe-area). Uses a helper .orio-mobile-pb class defined in
          index.css so the safe-area env() can compose with the responsive
          breakpoint (Tailwind can't easily nest env() + max() with md:). */}
      <main
        className="flex-1 relative z-10 flex flex-col min-h-0 min-w-0 w-full overflow-x-hidden orio-mobile-pb"
      >
        {children}
      </main>

      <MobileNav />
      <WelcomeCompleteSheet />
      <OnboardingTour />
      <WelcomeNewUserModal userName={user?.name} dogName={null} />
      <KarmaBurst />
      <SwUpdateToast />
      <PushOptInSheet />
    </div>
  );
}
